import { EditIcon } from '@arnold/common';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { NewReport } from '../../hooks/useNewReport';
import { DockDivider, DockPill, DockRoot, DockStatus, ErrorBubble, Fab, StatusDot } from './editorStyles';
import { HistoryCard } from './HistoryCard';
import { useEditor } from './OverviewEditorContext';
import { Action, QuietAction } from './styles';

/** Floating pencil that turns into the edit-mode menu (save, discard, history, metrics). */
export function EditorDock({ report }: { report: NewReport }) {
  const { t } = useTranslation('newReport');
  const editor = useEditor();
  const { controller } = editor;
  const [historyOpen, setHistoryOpen] = useState(false);
  if (!controller.canEdit || !report.rawData) return null;
  if (!controller.open)
    return (
      <DockRoot>
        <Fab type="button" title={t('definition.editReport')} onClick={controller.toggle}>
          <EditIcon />
        </Fab>
      </DockRoot>
    );
  const status = controller.validationError
    ? t('definition.invalid', { reason: t(`definition.validation.${controller.validationError}`) })
    : t(controller.dirty ? 'definition.unsaved' : controller.savedId ? 'definition.saved' : 'definition.editMode');
  return (
    <DockRoot>
      {controller.error && <ErrorBubble>{controller.error}</ErrorBubble>}
      {historyOpen && <HistoryCard report={report} onClose={() => setHistoryOpen(false)} />}
      <DockPill>
        {controller.previewId ? (
          <>
            <DockStatus>
              <StatusDot warn />
              {t('definition.previewing', { id: controller.previewId })}
            </DockStatus>
            <DockDivider />
            <Action active={historyOpen} onClick={() => setHistoryOpen((open) => !open)}>
              {t('definition.history')}
            </Action>
            <Action onClick={controller.exitPreview}>{t('definition.exitPreview')}</Action>
            <Action primary onClick={controller.editPreview}>
              {t('definition.editVersion')}
            </Action>
          </>
        ) : (
          <>
            <DockStatus title={status}>
              <StatusDot warn={controller.dirty || !!controller.validationError} />
              {status}
            </DockStatus>
            <DockDivider />
            <Action active={historyOpen} onClick={() => setHistoryOpen((open) => !open)}>
              {t('definition.history')}
            </Action>
            <Action onClick={() => editor.setModal({ kind: 'library' })}>{t('definition.metricsLibrary')}</Action>
            <DockDivider />
            <QuietAction onClick={controller.discard}>{t('definition.discard')}</QuietAction>
            <Action
              primary
              disabled={controller.busy || !!controller.validationError}
              onClick={() => editor.setModal({ kind: 'save' })}
            >
              {t('definition.saveVersion')}
            </Action>
          </>
        )}
      </DockPill>
    </DockRoot>
  );
}
